import appConfig from '@/config/appConfig.typed';
import flowAvailabilityService from '@/services/aem/flowAvailabilityService';
import marketConfigService from '@/services/marketConfigService';
import appRouterService from '@/services/route/appRouterService';
import userBrowserService from '@/services/user/userBrowserService';
import {showConnectionSelectionModalAction} from '@/state/slices/modalSlice';
import {dispatch} from '@/state/store';

import connectivityService from './connectivityService';

const CONNECTION_FLOW_TYPES = {
    SHOW_SELECTION_PAGE: 'showSelectionPage',
    REDIRECT_TO_BLE: 'redirectToBLE',
    REDIRECT_TO_HID: 'redirectToHID',
    NO_SUPPORTED_CONNECTION: 'noSupportedConnection',
};

const isBleEnabledForDevice = (deviceType) => {
    const {isBleDisabled} = marketConfigService.getDeviceData(deviceType) || {};

    //physical ble connection availability on the device check
    return !isBleDisabled;
};

const isBleConnectionSupported = (deviceType) => {
    //is connection flow enabled for market
    const isConnectionEnabled = flowAvailabilityService.isConnectionEnabled();

    if (isConnectionEnabled) {
        const {isBleAvailable} = getConnectionAvailability(deviceType);

        return isBleAvailable;
    }

    return isBleEnabledForDevice(deviceType);
};

const isHidConnectionSupported = (deviceType) => {
    //is connection flow enabled for market
    const isConnectionEnabled = flowAvailabilityService.isConnectionEnabled();

    if (isConnectionEnabled) {
        const {isHidAvailable} = getConnectionAvailability(deviceType);

        return isHidAvailable;
    }

    return userBrowserService.isHidSupported();
};

const handleConnectionFlow = (deviceType) => {
    const flowType = getConnectionFlowType(deviceType);

    switch (flowType) {
        case CONNECTION_FLOW_TYPES.SHOW_SELECTION_PAGE:
            showConnectionSelectionModal(deviceType);
            break;
        case CONNECTION_FLOW_TYPES.REDIRECT_TO_BLE:
            redirectToBle(deviceType);
            break;
        case CONNECTION_FLOW_TYPES.REDIRECT_TO_HID:
            redirectToHid(deviceType);
            break;
        default:
            // showUnsupportedBrowserMessage();
            break;
    }
};

const showConnectionSelectionModal = (deviceType) => {
    dispatch(showConnectionSelectionModalAction({deviceType}));
};

const redirectToBle = (deviceType) => {
    connectivityService.setIsBle();
    appRouterService.forwardToConnectionGuidePage(deviceType);
};

const redirectToHid = (deviceType) => {
    connectivityService.setIsUsb();
    appRouterService.forwardToConnectionGuidePage(deviceType);
};

const getConnectionFlowType = (deviceType) => {
    const {isBleAvailable, isHidAvailable} = getConnectionAvailability(deviceType);

    if (isBleAvailable && isHidAvailable) {
        return CONNECTION_FLOW_TYPES.SHOW_SELECTION_PAGE;
    } else if (isBleAvailable) {
        return CONNECTION_FLOW_TYPES.REDIRECT_TO_BLE;
    } else if (isHidAvailable) {
        return CONNECTION_FLOW_TYPES.REDIRECT_TO_HID;
    } else {
        return CONNECTION_FLOW_TYPES.NO_SUPPORTED_CONNECTION;
    }
};

const getConnectionAvailability = (deviceType) => {
    const isBleSupported = userBrowserService.isBluetoothSupported() && isBleEnabledForDevice(deviceType);
    const isHidSupported = userBrowserService.isHidSupported();

    const connectionAvailabilityList = appConfig.getConnectionAvailabilityList();
    const isBleAvailablePerDevice = flowAvailabilityService.isAvailablePerDevice(
        connectionAvailabilityList,
        deviceType,
        true
    );
    const isBleAvailable = isBleAvailablePerDevice && isBleSupported;
    const isHidAvailablePerDevice = flowAvailabilityService.isAvailablePerDevice(
        connectionAvailabilityList,
        deviceType
    );
    const isHidAvailable = isHidAvailablePerDevice && isHidSupported;

    return {isBleAvailable, isHidAvailable};
};

const isConnectionAvailable = (deviceType) => {
    const {isBleAvailable, isHidAvailable} = getConnectionAvailability(deviceType);

    return isBleAvailable || isHidAvailable;
};

export default {
    handleConnectionFlow,
    redirectToBle,
    redirectToHid,
    isBleEnabledForDevice,
    isHidConnectionSupported,
    isBleConnectionSupported,
    isConnectionAvailable,
};
